import {css, Link} from '@sail/ui/deprecated';
import {view, Icon, List, ListItem} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {paymentLink, email, call} from '@sail/icons/react/Icon';
import {
  Card,
  ThemeProvider,
  useTheme,
  Theme,
  LinkTheme,
} from '@stripe-internal/conversations-ui';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {defineMessages, FormattedMessage} from 'react-intl';
import {IconAsset} from '@sail/icons/types';

export type ValueProps = {
  merchant_link?: string;
  merchant_email?: string;
  merchant_number?: string;
  merchant_name?: string;
  is_stripe_managed_payment?: boolean;
};

const messages = defineMessages({
  contactMerchantInstructionsWithContactInfo: {
    id: 'mse_inline_data.universal_help_purchase_contact_details.contact_merchant_with_contact_info',
    description:
      'Message directing user to contact the business. This message is shown with contact details for the business.',
    defaultMessage:
      'For questions about a purchase made through Link, you will need to reach out to the business directly for help. Here are the contact details for {merchant}:',
  },
  contactMerchantInstructions: {
    id: 'mse_inline_data.universal_help_purchase_contact_details.contact_merchant',
    description: 'Message directing user to contact the business.',
    defaultMessage:
      'For questions about a purchase made through Link, you will need to reach out to {merchant} directly for help.',
  },
  stripeManagedTransactionInfo: {
    id: 'mse_inline_data.universal_help_purchase_contact_details.mor_transaction_info',
    description: 'Message when its an MoR transaction.',
    defaultMessage: 'Our support team can help you with this purchase.',
  },
});

export const ContactLinkListItem = ({
  theme,
  testId,
  icon,
  linkCopy,
  href,
}: {
  theme: Theme;
  testId: string;
  icon: IconAsset;
  linkCopy?: string;
  href?: string;
}) => {
  const analytics = useAnalytics();

  if (!href || !linkCopy) {
    return <></>;
  }

  return (
    <ListItem
      data-testid={testId}
      title={
        <Link
          css={{color: css.value(theme.linkColor)}}
          onPress={() => {
            safeWindowOpen(href);
            analytics.action('purchase_contact_details_click', {
              linkCopy,
            });
          }}
        >
          <view.div
            css={{
              textOverflow: 'ellipsis',
              display: 'block',
              overflow: 'hidden',
              width: 'fill',
              font: 'label.small.emphasized',
            }}
          >
            {linkCopy}
          </view.div>
        </Link>
      }
      icon={<Icon size="xsmall" icon={icon} />}
      css={{
        ':last-child': {border: 'none'},
        paddingX: 'space.0',
      }}
    />
  );
};

const LinkPurchaseContactDetails = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const value = datum.value as ValueProps;
  const analytics = useAnalytics();

  const {
    merchant_link: merchantLink,
    merchant_email: merchantEmail,
    merchant_number: merchantNumber,
    merchant_name: merchantName,
    is_stripe_managed_payment: isStripeManagedPayment,
  } = value;

  const theme = useTheme();
  const hasContactInfo = merchantLink || merchantNumber || merchantEmail;

  analytics.viewed('purchase_contact_details', {
    contactInfoShown: !!hasContactInfo,
  });

  if (isStripeManagedPayment) {
    return (
      <view.div css={{gap: 'medium', stack: 'y'}}>
        <view.div data-testid="managed-payment-message">
          <FormattedMessage {...messages.stripeManagedTransactionInfo} />
        </view.div>
      </view.div>
    );
  }

  if (!hasContactInfo) {
    return (
      <view.div css={{gap: 'medium', stack: 'y'}}>
        <view.div data-testid="contact-instructions">
          <FormattedMessage
            {...messages.contactMerchantInstructions}
            values={{merchant: merchantName}}
          />
        </view.div>
      </view.div>
    );
  }

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <view.div data-testid="contact-instructions" css={{font: 'label.medium'}}>
        <FormattedMessage
          {...messages.contactMerchantInstructionsWithContactInfo}
          values={{merchant: merchantName}}
        />
      </view.div>
      <Card>
        <List css={{bleedY: 'medium', bleedX: 'xsmall'}}>
          <ContactLinkListItem
            testId="merchant-link"
            linkCopy={merchantLink}
            href={merchantLink}
            theme={theme}
            icon={paymentLink}
          />
          <ContactLinkListItem
            testId="merchant-email"
            linkCopy={merchantEmail}
            href={`mailto:${merchantEmail}`}
            theme={theme}
            icon={email}
          />
          <ContactLinkListItem
            testId="merchant-phone"
            linkCopy={merchantNumber}
            href={`tel:${merchantNumber}`}
            theme={theme}
            icon={call}
          />
        </List>
      </Card>
    </view.div>
  );
};

export const PurchaseContactDetails = (
  props: InlineDatumReactComponentProps,
) => {
  return (
    <ThemeProvider value={LinkTheme}>
      <LinkPurchaseContactDetails props={props} />
    </ThemeProvider>
  );
};
